import { Overlay } from "@angular/cdk/overlay";
import { Platform } from "@angular/cdk/platform";
import { Location } from "@angular/common";
import {
  Directive,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Optional,
  Output,
  Renderer2,
  SimpleChanges,
  ViewContainerRef,
} from "@angular/core";
import { ANIMATION_MODULE_TYPE } from "@angular/platform-browser/animations";
import { NzConfigService, WithConfig } from "ng-zorro-antd/core/config";
import { IndexableObject } from "ng-zorro-antd/core/types";
import { InputBoolean } from "ng-zorro-antd/core/util";
import { NzDropDownDirective } from "ng-zorro-antd/dropdown";
import { Subscription, SubscriptionLike, fromEvent } from "rxjs";
import { UiDropdownMenuComponent } from "./components/dropdown-menu/dropdown-menu.component";
import { UiDropdownTrigger, UiPlacementType } from "./models/dropdown.models";

@Directive({
  selector: "[ui-dropdown]",
  exportAs: "uiDropdown",
  standalone: true,
})
export class UiDropDownDirective extends NzDropDownDirective implements OnDestroy, OnChanges {
  @Input("uiDropdownMenu") public nzDropdownMenu: UiDropdownMenuComponent | null = null;
  @Input("uiTrigger") public nzTrigger: UiDropdownTrigger = "hover";
  @Input("uiMatchWidthElement") public nzMatchWidthElement: ElementRef | null = null;
  /**
   * @deprecated Not supported, use `nzBackdrop` instead.
   * @breaking-change 13.0.0
   */
  @Input("uiHasBackdrop") @InputBoolean() public nzHasBackdrop = false;
  @Input("uiBackdrop") @WithConfig<boolean>() @InputBoolean() public nzBackdrop = false;
  @Input("uiClickHide") @InputBoolean() public nzClickHide = true;
  @Input("uiDisabled") @InputBoolean() public nzDisabled = false;
  @Input("uiVisible") @InputBoolean() public nzVisible = false;
  @Input("uiOverlayClassName") public nzOverlayClassName = "";
  @Input("uiOverlayStyle") public nzOverlayStyle: IndexableObject = {};
  @Input("uiPlacement") public nzPlacement: UiPlacementType = "bottomLeft";
  @Output("uiVisibleChange") public readonly nzVisibleChange: EventEmitter<boolean> = new EventEmitter();

  private locationChange: SubscriptionLike;
  private scrollSubscription: Subscription | null = null;

  constructor(
    public readonly nzConfigService: NzConfigService,
    public elementRef: ElementRef,
    overlay: Overlay,
    renderer: Renderer2,
    viewContainerRef: ViewContainerRef,
    platform: Platform,
    private location: Location,
    @Optional() @Inject(ANIMATION_MODULE_TYPE) animationType: string,
    private ngZone: NgZone
  ) {
    super(nzConfigService, elementRef, overlay, renderer, viewContainerRef, platform);

    if (animationType === "NoopAnimations") {
      this.handleNoopAnimations();
    }
  }

  private handleNoopAnimations(): void {
    // if animations are turned off, the logic supposed to be performed on `animationStateChange` in the ng-zorro dropdown.directive implementation
    // is not performed, so we are mimicking it here
    this.locationChange = this.location.subscribe(() => {
      this.nzDropdownMenu?.onAnimationEvent({
        toState: "void",
        fromState: undefined,
        disabled: undefined,
        element: undefined,
        phaseName: undefined,
        totalTime: undefined,
        triggerName: undefined,
      });
    });
  }

  /**
   *  @link https://quantive-inc.atlassian.net/browse/GVS-39009
   *
   * overriding with our own listeners to use capturing strategy
   * helps us catch scroll events from nested containers and not only the document itself
   */
  private handleNzVisibleChange(visible: boolean): void {
    if (visible) {
      this.addScrollListener();
    } else {
      this.removeScrollListener();
    }
  }

  private addScrollListener(): void {
    this.scrollSubscription = this.ngZone.runOutsideAngular(() => {
      return fromEvent(document, "scroll", { capture: true }).subscribe(() => {
        this["overlayRef"].updatePosition();
      });
    });
  }

  private removeScrollListener(): void {
    if (this.scrollSubscription) {
      this.scrollSubscription.unsubscribe();
      this.scrollSubscription = null;
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);

    const { nzVisible } = changes;
    if (nzVisible) {
      this.handleNzVisibleChange(nzVisible.currentValue);
    }
  }

  public ngOnDestroy(): void {
    super.ngOnDestroy();

    if (this.locationChange) {
      this.locationChange.unsubscribe();
      this.locationChange = null;
    }

    this.removeScrollListener();
  }
}
